import type { FC, ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { CheckCheck, Copy, ExternalLink, Info, MessageCircle, Phone } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { getFollowUpBucket } from '@/lib/clinic/selectors'
import { formatDate, formatDateTime, formatTime } from '@/lib/clinic/datetime'
import { formatPhone, joinList, telHref } from '@/lib/clinic/format'
import { buildWhatsAppUrl, fillTemplate, toWhatsAppNumber } from '@/lib/clinic/whatsapp'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import Field from '@/components/common/field'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface WhatsAppDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patientId: string | null
    followUpId: string | null
}

interface WhatsAppBodyProps {
    patientId: string | null
    followUpId: string | null
    onClose: () => void
}

const WhatsAppBody: FC<WhatsAppBodyProps> = ({ patientId, followUpId, onClose }): ReactNode => {
    const { data, index, actions, today } = useClinic()
    const { settings } = data
    const patient = patientId ? index.patientsById.get(patientId) ?? null : null
    const followUp = followUpId ? index.followUpsById.get(followUpId) ?? null : null
    const latestConsultation = patient ? index.consultationsByPatient.get(patient.id)?.[0] ?? null : null

    const templateValues = useMemo(() => ({
        patientName: patient?.name ?? '',
        clinicName: settings.clinicName,
        doctorName: settings.doctorName,
        followUpDate: followUp ? formatDate(followUp.dueDate) : 'your next visit',
        treatment: followUp?.treatment || (latestConsultation ? joinList(latestConsultation.treatments) : '') || 'dental treatment',
        clinicPhone: settings.phone
    }), [followUp, latestConsultation, patient?.name, settings.clinicName, settings.doctorName, settings.phone])

    const initialTemplateId = useMemo(() => {
        const has = (id: string) => settings.templates.some(item => item.id === id)

        if (followUp) {
            const bucket = getFollowUpBucket(followUp, today)
            if (bucket === 'overdue' && has('tpl-missed')) return 'tpl-missed'
            if (bucket === 'today' && has('tpl-due-today')) return 'tpl-due-today'
        }

        return has(settings.defaultTemplateId) ? settings.defaultTemplateId : settings.templates[0]?.id ?? ''
    }, [followUp, settings.defaultTemplateId, settings.templates, today])

    const [templateId, setTemplateId] = useState(initialTemplateId)

    const [message, setMessage] = useState(() => {
        const template = settings.templates.find(item => item.id === initialTemplateId)

        return template ? fillTemplate(template.body, templateValues) : ''
    })

    if (!patient) {
        return (
            <div className='p-6'>
                <DialogTitle>Patient not found</DialogTitle>

                <DialogDescription className='mt-2'>
                    This patient record is no longer available.
                </DialogDescription>
            </div>
        )
    }

    const number = toWhatsAppNumber(patient.phone, settings.countryCode)
    const reminders = (followUp ? index.remindersByFollowUp.get(followUp.id) : index.remindersByPatient.get(patient.id))?.slice(0, 3) ?? []
    const templateItems = settings.templates.map(item => ({ value: item.id, label: item.name }))

    const applyTemplate = (id: string) => {
        const template = settings.templates.find(item => item.id === id)

        setTemplateId(id)
        if (template) setMessage(fillTemplate(template.body, templateValues))
    }

    const openWhatsApp = () => {
        if (!number || !message.trim()) return

        window.open(buildWhatsAppUrl(number, message.trim()), '_blank', 'noopener,noreferrer')
        actions.logReminder({ patientId: patient.id, followUpId: followUp?.id ?? null, channel: 'whatsapp', message })
        toast.success('WhatsApp opened', { description: 'Press send in WhatsApp. This reminder is saved in the history.' })
        onClose()
    }

    const callPatient = () => {
        actions.logReminder({ patientId: patient.id, followUpId: followUp?.id ?? null, channel: 'call', message: '' })
        window.location.href = telHref(patient.phone)
        onClose()
    }

    const copyMessage = async () => {
        try {
            await navigator.clipboard.writeText(message)
            toast.success('Message copied')
        }
        catch {
            toast.error('Could not copy. Select the text and copy it manually.')
        }
    }

    return (
        <>
            <div className='flex items-start gap-3 border-b px-5 py-4 pr-12'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-whatsapp/10 text-whatsapp'>
                    <MessageCircle className='size-5' />
                </span>

                <div className='flex min-w-0 flex-col gap-1'>
                    <DialogTitle className='text-lg font-semibold'>
                        WhatsApp reminder
                    </DialogTitle>

                    <DialogDescription>
                        To {patient.name} · {number ? `+${number}` : formatPhone(patient.phone)}
                    </DialogDescription>
                </div>
            </div>

            <div className='grid flex-1 gap-5 overflow-y-auto px-5 py-5 md:grid-cols-2'>
                <div className='flex flex-col gap-4'>
                    {templateItems.length > 0 && (
                        <Field label='Message template' htmlFor='whatsapp-template'>
                            <Select items={templateItems} value={templateId} onValueChange={next => next && applyTemplate(next)}>
                                <SelectTrigger id='whatsapp-template' className='w-full'>
                                    <SelectValue />
                                </SelectTrigger>

                                <SelectContent alignItemWithTrigger={false}>
                                    {templateItems.map(item => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    )}

                    <Field label='Message' htmlFor='whatsapp-message' hint='You can edit the message before opening WhatsApp.'>
                        <Textarea
                            id='whatsapp-message'
                            rows={7}
                            value={message}
                            onChange={event => setMessage(event.target.value)}
                            className='min-h-40'
                        />
                    </Field>
                </div>

                <div className='flex flex-col gap-4'>
                    <div>
                        <p className='mb-1.5 text-[13px] font-medium text-foreground'>
                            Preview
                        </p>

                        <div className='rounded-xl bg-[#e7f0ec] p-4'>
                            <div className='ml-auto max-w-[92%] rounded-xl rounded-tr-sm bg-[#d9fdd3] px-3 py-2 text-[13px] leading-relaxed text-[#111b21] shadow-sm'>
                                <p className='whitespace-pre-wrap break-words'>
                                    {message.trim() || 'Your message will appear here.'}
                                </p>

                                <p className='mt-1 flex items-center justify-end gap-1 text-[11px] text-[#667781]'>
                                    {formatTime(new Date())}
                                    <CheckCheck className='size-3.5 text-[#53bdeb]' />
                                </p>
                            </div>
                        </div>
                    </div>

                    <p className='flex gap-2 rounded-lg bg-info-soft p-3 text-xs leading-relaxed text-foreground'>
                        <Info className='mt-0.5 size-4 shrink-0 text-info' />
                        WhatsApp opens with this message ready. Nothing is sent until you press send in WhatsApp.
                    </p>

                    {!number && (
                        <p className='text-xs font-medium text-destructive'>
                            This patient has no valid phone number. Update the patient details first.
                        </p>
                    )}

                    {reminders.length > 0 && (
                        <div>
                            <p className='mb-1.5 text-[13px] font-medium text-foreground'>
                                Recent contact
                            </p>

                            <ul className='flex flex-col gap-1.5'>
                                {reminders.map(reminder => (
                                    <li key={reminder.id} className='flex items-center gap-2 text-xs text-muted-foreground'>
                                        {reminder.channel === 'whatsapp'
                                            ? <MessageCircle className='size-3.5 text-whatsapp' />
                                            : <Phone className='size-3.5 text-info' />}
                                        {reminder.channel === 'whatsapp' ? 'WhatsApp opened' : 'Phone call'} · {formatDateTime(reminder.at)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <div className='flex flex-col-reverse gap-2 border-t bg-muted/40 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex flex-col-reverse gap-2 sm:flex-row'>
                    <Button type='button' variant='ghost' onClick={copyMessage} disabled={!message.trim()}>
                        <Copy />
                        Copy message
                    </Button>

                    <Button type='button' variant='outline' onClick={callPatient}>
                        <Phone />
                        Call instead
                    </Button>
                </div>

                <Button
                    type='button'
                    size='lg'
                    onClick={openWhatsApp}
                    disabled={!number || !message.trim()}
                    className='bg-whatsapp text-white hover:bg-whatsapp/90'
                >
                    <ExternalLink />
                    Open WhatsApp
                </Button>
            </div>
        </>
    )
}

const WhatsAppDialog: FC<WhatsAppDialogProps> = ({ open, onOpenChange, patientId, followUpId }): ReactNode => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl'>
                <WhatsAppBody patientId={patientId} followUpId={followUpId} onClose={() => onOpenChange(false)} />
            </DialogContent>
        </Dialog>
    )
}

export default WhatsAppDialog
